// frontend/src/pages/ForBusinesses.js
// /for-businesses — public marketing page for salon/studio owners, restyled to
// mirror the premium OGESTYLE landing (cream, navy, Fraunces + DM Sans).
// NOTE: hero/section photos use /landing/*.jpg placeholders — swap for
// final provided photography.

import React from "react";
import { Link, useNavigate } from "react-router-dom";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import {
  FaStoreAlt,
  FaUserFriends,
  FaCreditCard,
  FaChartLine,
  FaTags,
  FaCalendarAlt,
  FaCheckCircle,
  FaArrowRight,
} from "react-icons/fa";
import "./ForBusinesses.css";
import ProtectRevenue from "../components/ProtectRevenue";

const IMG = {
  hero: "/landing/forbiz-hero.jpg",
  freelance: "/landing/freelance.jpg",
};

const SUPPORT_EMAIL = "support@mg.ogestyle.com";

const benefits = [
  { icon: <FaStoreAlt />, title: "Multi-location management", desc: "Run every shop location from a single dashboard." },
  { icon: <FaUserFriends />, title: "Staff management", desc: "Hire, schedule and manage your whole team." },
  { icon: <FaCreditCard />, title: "Payments & payouts", desc: "Commission tracking, split payments and automatic payouts." },
  { icon: <FaChartLine />, title: "Business analytics", desc: "Revenue, bookings and performance by location." },
  { icon: <FaTags />, title: "Packages & memberships", desc: "Service packages and plans for recurring revenue." },
  { icon: <FaCalendarAlt />, title: "Smart scheduling", desc: "Coordinate calendars across staff and locations." },
];

const trust = [
  "One dashboard for every location",
  "All your professionals included",
  "Products & product sales",
  "Automatic email & SMS reminders",
  "Secure payments by Stripe",
];

const features = [
  "Multi-location support",
  "Staff hiring and management",
  "Advanced booking system",
  "Commission and payout controls",
  "Client database",
  "Detailed reporting",
  "Waitlist management",
  "Service packages",
  "Product sales",
  "Online payments",
];

const ownerChecks = [
  "Have a registered business license",
  "Own or operate one or more physical locations",
  "Need to hire and manage staff members",
  "Want advanced operational tools and reporting",
];

const freelancerChecks = [
  "Work independently without a business license",
  "Don't have a physical location yet",
  "Want to start and grow your own brand quickly",
];

function ForBusinesses() {
  const navigate = useNavigate();

  return (
    <>
      <CustomerHeader />
      <main className="fb-page">
        {/* Hero */}
        <section className="fb-hero">
          <div className="fb-wrap fb-hero-grid">
            <div>
              <p className="fb-eyebrow">For salon &amp; studio owners</p>
              <h1 className="fb-display">Grow your beauty business</h1>
              <p className="fb-sub">
                Run one salon or many — locations, your team, bookings and
                payments in one calm, connected place.
              </p>
              <div className="fb-ctas">
                <button
                  type="button"
                  className="fb-btn fb-btn-primary"
                  onClick={() => navigate("/register-business")}
                >
                  Register your business
                </button>
                <a className="fb-link" href={`mailto:${SUPPORT_EMAIL}`}>
                  Talk to us
                </a>
                <Link className="fb-link" to="/plans">
                  See pricing
                </Link>
              </div>
            </div>

            <div className="fb-hero-art">
              <span className="fb-blob" />
              <img
                className="fb-hero-img"
                src={IMG.hero}
                alt="A stylist consulting a client in a bright, modern salon"
              />
              <div className="fb-chip-card">
                <span className="fb-chip-ic"><FaStoreAlt /></span>
                <div>
                  <p className="fb-chip-title">One dashboard</p>
                  <p className="fb-chip-sub">Every location &amp; team member</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust chips */}
        <section className="fb-wrap fb-trust" aria-label="Platform highlights">
          {trust.map((t) => (
            <span key={t}><FaCheckCircle /> {t}</span>
          ))}
        </section>

        {/* Why choose */}
        <section className="fb-sec fb-sec--white">
          <div className="fb-wrap">
            <div className="fb-sec-head">
              <p className="fb-eyebrow">Why OGESTYLE</p>
              <h2 className="fb-display">Everything a growing business needs</h2>
            </div>
            <div className="fb-grid">
              {benefits.map((b) => (
                <article className="fb-card" key={b.title}>
                  <span className="fb-ic">{b.icon}</span>
                  <h3>{b.title}</h3>
                  <p>{b.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Features (navy) */}
        <section className="fb-navy">
          <div className="fb-wrap">
            <p className="fb-eyebrow">Platform features</p>
            <h2 className="fb-display">Everything, built in</h2>
            <div className="fb-feat">
              {features.map((f) => (
                <div key={f}><FaCheckCircle /> {f}</div>
              ))}
            </div>
          </div>
        </section>

        {/* Protect your revenue */}
        <ProtectRevenue />

        {/* Compare */}
        <section className="fb-sec fb-sec--white">
          <div className="fb-wrap">
            <div className="fb-sec-head fb-center">
              <p className="fb-eyebrow">Which account is right for you?</p>
              <h2 className="fb-display">Owner or independent pro?</h2>
            </div>
            <div className="fb-compare">
              <article className="fb-panel fb-panel--navy">
                <h3>Choose Business Owner if you…</h3>
                <ul>
                  {ownerChecks.map((c) => (
                    <li key={c}><FaCheckCircle /> {c}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="fb-btn fb-btn-light"
                  onClick={() => navigate("/register-business")}
                >
                  Register your business
                </button>
              </article>

              <article className="fb-panel fb-panel--cream">
                <div className="fb-panel-img">
                  <img src={IMG.freelance} alt="An independent beauty professional at work" />
                </div>
                <h3>Choose Freelancer if you…</h3>
                <ul>
                  {freelancerChecks.map((c) => (
                    <li key={c}><FaCheckCircle /> {c}</li>
                  ))}
                </ul>
                <Link className="fb-panel-link" to="/freelancer">
                  Learn about the freelancer path <FaArrowRight />
                </Link>
              </article>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="fb-wrap fb-cta-wrap">
          <div className="fb-cta-band">
            <div>
              <h2 className="fb-display">Ready to grow your business?</h2>
              <p className="fb-cta-desc">
                Register in under 30 minutes. No contract — cancel anytime.
              </p>
            </div>
            <div className="fb-cta-actions">
              <button
                type="button"
                className="fb-btn fb-btn-primary"
                onClick={() => navigate("/register-business")}
              >
                Register your business
              </button>
              <a
                className="fb-btn fb-btn-outline"
                href="https://www.ogestyle.com/demo"
                rel="noreferrer noopener"
              >
                Book a live walkthrough
              </a>
            </div>
          </div>
        </section>
      </main>
      <CustomerFooter />
    </>
  );
}

export default ForBusinesses;
